import {
  getAppSettingsFromDBFromQC,
  getAppSettingsFromQC,
  getAppSettingsSchemaFromQC,
  updateAppSettings as updateQCAppSettings,
  useAppSettingsFromDB,
  useAppSettingsQuery
} from "@certa/queries/hooks/appSettings.hooks";
import {
  AlertCategorySchema,
  BusinessUnitSchema,
  CommentFlagOptionSchema,
  createValidatedSchema,
  EmailAttachmentSchema,
  EmailTemplateSchema,
  ExtraJSONSchema,
  ObjectTypeWorkflowKindRelationSchema,
  ProcessKindSchema,
  ProcessPDFTemplateSchema,
  ProcessStatusSchema,
  RegionSchema,
  UserGroupSchema,
  type AlertCategory,
  type AppSettings,
  type BusinessUnit,
  type CommentFlagOption,
  type EmailAttachment,
  type EmailTemplate,
  type ExtraJSON,
  type ObjectTypeWorkflowKindRelation,
  type ProcessKind,
  type ProcessPDFTemplate,
  type ProcessStatus,
  type Region,
  type UserGroup
} from "@certa/queries/types/appSettings.types";
import { studioState } from "../hooks/useStudioAPI";
import type { StudioEnvironments } from "@certa/studio-core";
import { studioAPIActions } from "../hooks/useStudioAPI/actions";
import { mergeAppSettingsWithReferenced } from "./appSettingsMerger";
import { useAppSettings } from "../hooks/useStudioAPI/hooks/dbOps.store";
import { useMemo } from "react";
import { StudioEnvBranchName } from "../constants/environmentBranches";

const { retriggerReferencedAppSettingsCalc } = studioAPIActions;

export const useGetAppSettings = (savepointId: number) => {
  const {
    data: appSettingsFromSavepoint,
    isLoading: isAppSettingsLoading,
    isError: isAppSettingsError
  } = useAppSettingsQuery(savepointId);
  const {
    data: appSettingsFromDB,
    isLoading: isAppSettingsFromDBLoading,
    isError: isAppSettingsFromDBError
  } = useAppSettingsFromDB();
  const referencedAppSettings = useAppSettings();

  const appSettings = useMemo(
    () =>
      mergeAppSettingsWithReferenced(
        appSettingsFromSavepoint || appSettingsFromDB,
        referencedAppSettings
      ),
    [appSettingsFromSavepoint, appSettingsFromDB, referencedAppSettings]
  );

  return {
    appSettings,
    isLoading: isAppSettingsLoading || isAppSettingsFromDBLoading,
    isError: isAppSettingsError && isAppSettingsFromDBError
  };
};

const updateAppSettings = async (
  appSettings: AppSettings,
  savepointId: number
) => {
  await updateQCAppSettings(appSettings, savepointId);
  retriggerReferencedAppSettingsCalc(appSettings, false);
};

export const getEnvironment = (
  baseBranchName?: StudioEnvBranchName
): StudioEnvironments => {
  // Default to development if no branch name is provided
  if (baseBranchName === StudioEnvBranchName.QA) {
    return "qa";
  }

  if (baseBranchName === StudioEnvBranchName.STAGING) {
    return "staging";
  }

  if (baseBranchName === StudioEnvBranchName.PRODUCTION) {
    return "production";
  }

  return "development";
};

export const getAppSettings = async ({
  branchSavePointId,
  skipMergingReferencedAppSettings = false
}: {
  branchSavePointId: number;
  skipMergingReferencedAppSettings?: boolean;
}) => {
  let appSettings;
  try {
    appSettings = await getAppSettingsFromQC(branchSavePointId);
  } catch {
    appSettings = await getAppSettingsFromDBFromQC();
  }

  if (!appSettings) {
    appSettings = await getAppSettingsFromDBFromQC();
  }

  const referencedAppSettings = studioState().getAppSettings();

  // Join app settings with referenced app settings, with referenced taking precedence
  return skipMergingReferencedAppSettings
    ? appSettings
    : mergeAppSettingsWithReferenced(appSettings, referencedAppSettings);
};

export const getAppSettingsUsergroups = async (branchSavePointId: number) => {
  const appSettings = await getAppSettings({ branchSavePointId });
  return appSettings?.group || [];
};

export const getAppSettingsStatuses = async (
  branchSavePointId: number
): Promise<ProcessStatus[]> => {
  const appSettings = await getAppSettings({ branchSavePointId });
  return appSettings?.processStatus || [];
};

export const getAppSettingsAdjudicationFlags = async (
  branchSavePointId: number
) => {
  const appSettings = await getAppSettings({ branchSavePointId });
  return appSettings?.commentFlagOption || [];
};

export const getAppSettingsEmailTemplates = async (
  branchSavePointId: number
) => {
  const appSettings = await getAppSettings({ branchSavePointId });
  return appSettings?.emailTemplate || [];
};

export const getAppSettingsObjectTypeWFKindRelations = async (
  branchSavePointId: number
) => {
  const appSettings = await getAppSettings({ branchSavePointId });
  return appSettings?.objectTypeWorkflowKindRelation || [];
};

//... Can add more if needed

export const addAppSettingsUserGroup = async (
  userGroup: UserGroup,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: UserGroupSchema,
    beJSONSchema: beSchema?.group || {},
    existingValues: appSettings.group
  });

  zodSchema.parse(userGroup);

  const newAppSettings = {
    ...appSettings,
    group: [...(appSettings?.group || []), userGroup]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const updateUserGroup = async (
  userGroup: UserGroup,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  if (!appSettings.group.find(ug => ug.name === userGroup.name)) {
    throw new Error("User group not found");
  }

  const zodSchema = createValidatedSchema({
    zodSchema: UserGroupSchema,
    beJSONSchema: beSchema?.group || {},
    existingValues: appSettings.group
  });

  zodSchema.parse(userGroup);

  const newAppSettings = {
    ...appSettings,
    group: appSettings.group.map(ug =>
      ug.name === userGroup.name ? userGroup : ug
    )
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addObjectTypeWFKindRelation = async (
  objectTypeWFKindRelation: ObjectTypeWorkflowKindRelation,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: ObjectTypeWorkflowKindRelationSchema,
    beJSONSchema: beSchema?.objectTypeWorkflowKindRelation || {},
    existingValues: appSettings.objectTypeWorkflowKindRelation
  });

  zodSchema.parse(objectTypeWFKindRelation);

  // Since this validation is not driven by BE Schema, adding this here explicitly
  if (
    appSettings.objectTypeWorkflowKindRelation.find(
      rel =>
        rel.objectType === objectTypeWFKindRelation.objectType &&
        rel.relation === objectTypeWFKindRelation.relation &&
        rel.workflowKind === objectTypeWFKindRelation.workflowKind
    )
  ) {
    throw new Error("Object type workflow kind relation already exists");
  }

  const newAppSettings = {
    ...appSettings,
    objectTypeWorkflowKindRelation: [
      ...(appSettings?.objectTypeWorkflowKindRelation || []),
      objectTypeWFKindRelation
    ]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const updateObjectTypeWFKindRelation = async (
  objectTypeWFKindRelation: ObjectTypeWorkflowKindRelation,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  if (
    !appSettings.objectTypeWorkflowKindRelation.find(
      rel =>
        rel.objectType === objectTypeWFKindRelation.objectType &&
        rel.relation === objectTypeWFKindRelation.relation &&
        rel.workflowKind === objectTypeWFKindRelation.workflowKind
    )
  ) {
    throw new Error("Object type workflow kind relation not found");
  }

  const zodSchema = createValidatedSchema({
    zodSchema: ObjectTypeWorkflowKindRelationSchema,
    beJSONSchema: beSchema?.objectTypeWorkflowKindRelation || {},
    existingValues: appSettings.objectTypeWorkflowKindRelation
  });

  zodSchema.parse(objectTypeWFKindRelation);

  const newAppSettings = {
    ...appSettings,
    objectTypeWorkflowKindRelation:
      appSettings.objectTypeWorkflowKindRelation.map(rel =>
        rel.objectType === objectTypeWFKindRelation.objectType &&
        rel.relation === objectTypeWFKindRelation.relation &&
        rel.workflowKind === objectTypeWFKindRelation.workflowKind
          ? objectTypeWFKindRelation
          : rel
      )
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsProcessKind = async ({
  processKind,
  branchSavePointId,
  shouldSkipIfExists = false
}: {
  processKind: ProcessKind;
  branchSavePointId: number;
  shouldSkipIfExists?: boolean;
}) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) {
    throw new Error("App settings not found");
  }

  if (
    shouldSkipIfExists &&
    appSettings.processKind.find(pk => pk.tag === processKind.tag)
  ) {
    return;
  }

  const zodSchema = createValidatedSchema({
    zodSchema: ProcessKindSchema,
    beJSONSchema: beSchema?.processKind || {},
    existingValues: appSettings?.processKind || []
  });

  zodSchema.parse(processKind);

  const newAppSettings: AppSettings = {
    ...appSettings,
    processKind: [...(appSettings?.processKind || []), processKind]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsProcessStatus = async (
  processStatus: ProcessStatus,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: ProcessStatusSchema,
    beJSONSchema: beSchema?.processStatus || {},
    existingValues: appSettings?.processStatus || []
  });

  zodSchema.parse(processStatus);

  const newAppSettings: AppSettings = {
    ...appSettings,
    processStatus: [...(appSettings?.processStatus || []), processStatus]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsEmailTemplate = async (
  emailTemplate: EmailTemplate,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: EmailTemplateSchema,
    beJSONSchema: beSchema?.emailTemplate || {},
    existingValues: appSettings?.emailTemplate || []
  });

  zodSchema.parse(emailTemplate);

  const newAppSettings: AppSettings = {
    ...appSettings,
    emailTemplate: [...(appSettings?.emailTemplate || []), emailTemplate]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsEmailAttachment = async (
  emailAttachment: EmailAttachment,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: EmailAttachmentSchema,
    beJSONSchema: beSchema?.emailAttachment || {},
    existingValues: appSettings?.emailAttachment || []
  });

  zodSchema.parse(emailAttachment);

  const newAppSettings: AppSettings = {
    ...appSettings,
    emailAttachment: [...(appSettings?.emailAttachment || []), emailAttachment]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsExtraJSON = async (
  extraJSON: ExtraJSON,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: ExtraJSONSchema,
    beJSONSchema: beSchema?.extraJSON || {},
    existingValues: appSettings?.extraJSON || []
  });

  zodSchema.parse(extraJSON);

  const newAppSettings: AppSettings = {
    ...appSettings,
    extraJSON: [...(appSettings?.extraJSON || []), extraJSON]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsBusinessUnit = async (
  businessUnit: BusinessUnit,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: BusinessUnitSchema,
    beJSONSchema: beSchema?.businessUnit || {},
    existingValues: appSettings?.businessUnit || []
  });

  zodSchema.parse(businessUnit);

  const newAppSettings: AppSettings = {
    ...appSettings,
    businessUnit: [...(appSettings?.businessUnit || []), businessUnit]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsRegion = async (
  region: Region,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: RegionSchema,
    beJSONSchema: beSchema?.region || {},
    existingValues: appSettings?.region || []
  });

  zodSchema.parse(region);

  const newAppSettings: AppSettings = {
    ...appSettings,
    region: [...(appSettings?.region || []), region]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsAlertCategory = async (
  alertCategory: AlertCategory,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: AlertCategorySchema,
    beJSONSchema: beSchema?.alertCategory || {},
    existingValues: appSettings?.alertCategory || []
  });

  zodSchema.parse(alertCategory);

  const newAppSettings: AppSettings = {
    ...appSettings,
    alertCategory: [...(appSettings?.alertCategory || []), alertCategory]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsProcessPDFTemplate = async (
  pdfTemplate: ProcessPDFTemplate,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: ProcessPDFTemplateSchema,
    beJSONSchema: beSchema?.processPDFTemplate || {},
    existingValues: appSettings?.processPDFTemplate || []
  });

  zodSchema.parse(pdfTemplate);

  const newAppSettings: AppSettings = {
    ...appSettings,
    processPDFTemplate: [
      ...(appSettings?.processPDFTemplate || []),
      pdfTemplate
    ]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const addAppSettingsCommentFlagOption = async (
  commentFlagOption: CommentFlagOption,
  branchSavePointId: number
) => {
  const [appSettings, beSchema] = await Promise.all([
    getAppSettings({ branchSavePointId }),
    getAppSettingsSchemaFromQC()
  ]);

  if (!appSettings) throw new Error("App settings not found");

  const zodSchema = createValidatedSchema({
    zodSchema: CommentFlagOptionSchema,
    beJSONSchema: beSchema?.commentFlagOption || {},
    existingValues: appSettings?.commentFlagOption || []
  });

  zodSchema.parse(commentFlagOption);

  const newAppSettings: AppSettings = {
    ...appSettings,
    commentFlagOption: [
      ...(appSettings?.commentFlagOption || []),
      commentFlagOption
    ]
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

export const deleteObjectTypeWFKindRelation = async (
  objectTypeWFKindRelation: ObjectTypeWorkflowKindRelation,
  branchSavePointId: number
) => {
  const appSettings = await getAppSettings({ branchSavePointId });

  if (!appSettings) throw new Error("App settings not found");

  const newAppSettings: AppSettings = {
    ...appSettings,
    objectTypeWorkflowKindRelation:
      appSettings.objectTypeWorkflowKindRelation.filter(
        rel =>
          !(
            rel.objectType === objectTypeWFKindRelation.objectType &&
            rel.workflowKind === objectTypeWFKindRelation.workflowKind &&
            rel.relation === objectTypeWFKindRelation.relation
          )
      )
  };

  await updateAppSettings(newAppSettings, branchSavePointId);
};

//... Can add more if needed
